import { useState, useEffect, useMemo } from 'react';

interface TourStep {
    element?: string;
    intro: string;
    position?: 'top' | 'bottom' | 'left' | 'right' | 'auto';
    tooltipClass?: string;
}

const getTourStepsForStartingAngle = (startingAngle: string, nodeId?: string): TourStep[] => {
    // Use nodeId to target the specific node if available
    const nodeSelector = nodeId ? `[data-id="${nodeId}"]` : '';

    switch (startingAngle) {
        case 'quickstart':
            if (nodeId) {
                return [
                    {
                        element: nodeSelector,
                        intro: 'Welcome to your Quick Start project! This is your Quick Start node.',
                        tooltipClass: 'introjs-custom-tooltip',
                        position: 'right'
                    },
                    {
                        element: `${nodeSelector} .challenge`,
                        intro: 'Start by describing your challenge in this text area.',
                        tooltipClass: 'introjs-custom-tooltip',
                        position: 'bottom'
                    },
                    {
                        element: `${nodeSelector} .desiredOutcome`,
                        intro: 'Then describe your desired outcome in this text area.',
                        tooltipClass: 'introjs-custom-tooltip',
                        position: 'bottom'
                    },
                    {
                        element: `${nodeSelector} [data-tour="proceed-button"]`,
                        intro: 'Once you\'ve filled in both fields, click the Proceed button to continue.',
                        tooltipClass: 'introjs-custom-tooltip',
                        position: 'top'
                    },
                    {
                        intro: 'After clicking Proceed, a document node will be created and your project will be initialized!',
                        tooltipClass: 'introjs-custom-tooltip'
                    }
                ];
            } else {
                // Fallback if nodeId is not available
                return [
                    {
                        intro: 'Welcome to your Quick Start project! Look for your Quick Start node on the canvas.',
                        tooltipClass: 'introjs-custom-tooltip'
                    },
                    {
                        intro: 'Click on the Quick Start node to open its form and fill in your project details.',
                        tooltipClass: 'introjs-custom-tooltip'
                    }
                ];
            }

        case 'quickstartdynamic':
            if (nodeId) {
                return [
                    {
                        element: nodeSelector,
                        intro: 'Welcome to your Dynamic Quick Start project! This node allows you to select templates and generate customized documents.',
                        tooltipClass: 'introjs-custom-tooltip',
                        position: 'right'
                    },
                    {
                        element: `${nodeSelector} select`,
                        intro: 'First, select a template from this dropdown that matches your project type.',
                        tooltipClass: 'introjs-custom-tooltip',
                        position: 'bottom'
                    },
                    {
                        element: `${nodeSelector} textarea:first-of-type`,
                        intro: 'Describe your challenge in detail - what obstacles are you facing?',
                        tooltipClass: 'introjs-custom-tooltip',
                        position: 'bottom'
                    },
                    {
                        element: `${nodeSelector} textarea:last-of-type`,
                        intro: 'Describe your desired outcome - how should things be in the end?',
                        tooltipClass: 'introjs-custom-tooltip',
                        position: 'bottom'
                    },
                    {
                        element: `${nodeSelector} .quickstart-proceed-button`,
                        intro: 'Click Proceed to generate a customized document based on your template and inputs.',
                        tooltipClass: 'introjs-custom-tooltip',
                        position: 'top'
                    },
                    {
                        intro: 'The system will generate a document with intelligent suggestions based on your chosen template and inputs!',
                        tooltipClass: 'introjs-custom-tooltip'
                    }
                ];
            } else {
                return [
                    {
                        intro: 'Welcome to your Dynamic Quick Start project! Look for your Dynamic Quick Start node on the canvas.',
                        tooltipClass: 'introjs-custom-tooltip'
                    },
                    {
                        intro: 'Click on the Dynamic Quick Start node to select a template and fill in your project details.',
                        tooltipClass: 'introjs-custom-tooltip'
                    }
                ];
            }

        case 'document':
            if (nodeId) {
                return [
                    {
                        element: nodeSelector,
                        intro: 'Welcome to your Document project! This is your Document node where you can create and edit content.',
                        tooltipClass: 'introjs-custom-tooltip',
                        position: 'right'
                    },
                    {
                        intro: 'Use the Document node to write, format, and structure your content using Markdown.',
                        tooltipClass: 'introjs-custom-tooltip'
                    }
                ];
            } else {
                return [
                    {
                        intro: 'Welcome to your Document project! Look for your Document node on the canvas.',
                        tooltipClass: 'introjs-custom-tooltip'
                    }
                ];
            }

        default:
            return [
                {
                    intro: `Welcome to your ${startingAngle} project! This guided tour will help you get started.`,
                    tooltipClass: 'introjs-custom-tooltip'
                },
                {
                    intro: 'Explore the interface and start building your project!',
                    tooltipClass: 'introjs-custom-tooltip'
                }
            ];
    }
};

export const useInitializationTour = (startingAngle: string, nodeId?: string) => {
    const [introEnabled, setIntroEnabled] = useState(false);
    const [introSteps, setIntroSteps] = useState<TourStep[]>([]);

    // Use useMemo to prevent unnecessary recalculation of steps
    const tourSteps = useMemo(() => {
        // Don't generate steps if we don't have a nodeId for quickstart
        if (startingAngle === 'quickstart' && !nodeId) {
            return [
                {
                    intro: 'Welcome to your Quick Start project! Look for your Quick Start node on the canvas.',
                    tooltipClass: 'introjs-custom-tooltip'
                },
                {
                    intro: 'Click on the Quick Start node to open its form and fill in your project details.',
                    tooltipClass: 'introjs-custom-tooltip'
                }
            ];
        }

        return getTourStepsForStartingAngle(startingAngle, nodeId);
    }, [startingAngle, nodeId]);

    // Only update steps when they actually change
    useEffect(() => {
        setIntroSteps(tourSteps);
    }, [tourSteps]);

    const introOptions = {
        nextLabel: 'Next →',
        prevLabel: '← Back',
        skipLabel: 'Skip Tour',
        doneLabel: 'Got it!',
        hidePrev: false,
        hideNext: false,
        tooltipClass: 'introjs-custom-tooltip',
        highlightClass: 'introjs-custom-highlight',
        exitOnEsc: false,
        exitOnOverlayClick: false,
        showStepNumbers: true,
        keyboardNavigation: true,
        showButtons: true,
        showBullets: true,
        showProgress: true,
        scrollToElement: true,
        scrollPadding: 30,
        overlayOpacity: 0.5,
        disableInteraction: false,
        helperElementPadding: 10,
        dontShowAgain: false,
        skipOnScroll: false
    };

    return { introEnabled, setIntroEnabled, introSteps, introOptions };
};

export default useInitializationTour;
